'use client';
import logger from '../../logger';
import publicEnv from '../../publicEnv';
import { getOwnerJWT } from '../../utils/DryCookies';

const { API_BASE } = publicEnv;

/**
 * uploadFilesToCloud — multipart/form-data upload of files to a note.
 *
 * Uses native fetch (not dryFetch) because dryFetch forces Content-Type: application/json.
 * The browser sets the multipart boundary automatically when FormData is used as the body.
 *
 * Endpoint: POST /upload
 * Body: FormData with note_id + one entry per file
 *
 * @param {{ note_id: string, files: File[] }} param0
 * @returns {Promise<object>} server response JSON
 */
export const uploadFilesToCloud = async ({ note_id, files }) => {
  logger.info({ note_id, fileCount: files?.length }, 'uploadFilesToCloud: starting upload');

  if (!note_id || !files?.length) {
    throw new Error('uploadFilesToCloud: missing required note_id or files');
  }

  const ownerJWT = await getOwnerJWT();
  const formData = new FormData();
  formData.append('note_id', note_id);
  files.forEach((file) => formData.append('files', file));

  const response = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ownerJWT}`,
      // Do NOT set Content-Type — browser sets it automatically with the boundary
    },
    mode: 'cors',
    body: formData,
  });

  if (!response.ok) {
    const error = new Error(`uploadFilesToCloud: HTTP ${response.status} from /upload`);
    error.status = response.status;
    logger.error({ note_id, status: response.status }, 'uploadFilesToCloud: upload failed');
    throw error;
  }

  const data = await response.json();
  logger.info({ note_id }, 'uploadFilesToCloud: upload succeeded');
  return data;
};
